import type {
  Duration,
  Extension,
  HealthcareService,
  LocationHoursOfOperation,
  Period,
  PractitionerRole,
  Schedule,
} from '@medplum/fhirtypes';

import { FHIR_TIMEZONE_EXTENSION } from './extensions';
import { ELUVE_FHIR_NAMESPACE } from './namespace';

// Engine-facing builders for the owned, rules-based availability model that
// Medplum's native $find/$hold/$confirm operations read. Ported from the
// orchard-ehr POC (ADR-0018/0019). Pure — no I/O; the seed (super-admin) and
// the backend materializer compose these and write them.
//
// The encoding mirrors the R5 `Availability` datatype as nested R4 extensions
// and restricts durations to a unit subset, so it lives in one place rather
// than being hand-written per call site.

// Medplum's SchedulingParameters extension carries the booking rules the engine
// reads (duration, alignment, buffers, timezone, recurring availability). This
// is the engine input — distinct from the eluve-authored
// `scheduling-parameters` extension the fhir-renderer reads for display.
export const MEDPLUM_SCHEDULING_PARAMETERS_EXTENSION =
  'https://medplum.com/fhir/StructureDefinition/SchedulingParameters';

// The load-bearing link: an extension on `Schedule.serviceType` whose
// valueReference points at the HealthcareService. `$find` binds a Schedule to a
// service purely by THIS reference, never the `.type` coding.
export const SERVICE_TYPE_REFERENCE_EXTENSION =
  'https://medplum.com/fhir/service-type-reference';

export const DAYS_OF_WEEK = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

// Sunday-first, matching the schedule calendar and `Date.getDay()` order.
// DAYS_OF_WEEK stays Monday-first because that is the FHIR code order.
export const WEEK_ORDER: readonly DayOfWeek[] = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
];

// A recurring availability window (mirrors PractitionerRole.availableTime / the
// R5 Availability type). Wall-clock "HH:mm:ss" in the schedule's timezone.
export type Availability = {
  daysOfWeek: DayOfWeek[];
  start: string; // "09:00:00"
  end: string; // "17:00:00"
};

// Duration units the engine accepts (ms/s too fine; mo/a ambiguous).
function minutes(value: number): Duration {
  return {
    value,
    unit: 'min',
    system: 'http://unitsofmeasure.org',
    code: 'min',
  };
}

function availabilityToExtension(a: Availability): Extension {
  return {
    url: 'availableTime',
    extension: [
      ...a.daysOfWeek.map((d) => ({ url: 'daysOfWeek', valueCode: d })),
      { url: 'availableStartTime', valueTime: a.start },
      { url: 'availableEndTime', valueTime: a.end },
    ],
  };
}

export type SchedulingParametersInput = {
  // Required on a Schedule's block (scopes the override to one service); omitted
  // on a HealthcareService's block (implied by the resource).
  service?: { reference: string; display?: string };
  durationMinutes?: number;
  alignmentIntervalMinutes?: number;
  alignmentOffsetMinutes?: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  // Interprets the availability windows. Anchor the alignment grid to clinic
  // local midnight by setting both timezone + alignmentTimezone to the clinic
  // zone, so the grid lands on clinic-local boundaries regardless of UTC offset.
  timezone?: string;
  alignmentTimezone?: string;
  availability?: Availability[];
};

// Build a single Medplum SchedulingParameters extension. Only provided fields
// are emitted, in the nested encoding the engine parses.
export function buildSchedulingParameters(
  input: SchedulingParametersInput,
): Extension {
  const extension: Extension[] = [];
  if (input.service)
    extension.push({ url: 'service', valueReference: input.service });
  if (input.timezone)
    extension.push({ url: 'timezone', valueCode: input.timezone });
  if (input.alignmentTimezone)
    extension.push({
      url: 'alignmentTimezone',
      valueCode: input.alignmentTimezone,
    });
  if (input.durationMinutes !== undefined)
    extension.push({
      url: 'duration',
      valueDuration: minutes(input.durationMinutes),
    });
  if (input.alignmentIntervalMinutes !== undefined)
    extension.push({
      url: 'alignmentInterval',
      valueDuration: minutes(input.alignmentIntervalMinutes),
    });
  if (input.alignmentOffsetMinutes !== undefined)
    extension.push({
      url: 'alignmentOffset',
      valueDuration: minutes(input.alignmentOffsetMinutes),
    });
  if (input.bufferBeforeMinutes !== undefined)
    extension.push({
      url: 'bufferBefore',
      valueDuration: minutes(input.bufferBeforeMinutes),
    });
  if (input.bufferAfterMinutes !== undefined)
    extension.push({
      url: 'bufferAfter',
      valueDuration: minutes(input.bufferAfterMinutes),
    });
  if (input.availability)
    extension.push({
      url: 'availability',
      extension: input.availability.map(availabilityToExtension),
    });
  return { url: MEDPLUM_SCHEDULING_PARAMETERS_EXTENSION, extension };
}

// One bookable offering on a clinician's schedule. On Medplum 5.1.26 the engine
// layers the Schedule's SchedulingParameters over the HealthcareService's, so
// `duration` DOES inherit and only errors when absent from both. We still copy
// duration/alignment/buffers onto the Schedule block so the offering's rules are
// pinned at materialization time rather than resolved at $find time.
export type ScheduleOffering = {
  reference: string;
  display?: string;
  durationMinutes: number;
  alignmentIntervalMinutes?: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
};

export type ClinicianScheduleInput = {
  id: string;
  // Exactly one actor is allowed (engine constraint); a PractitionerRole here.
  actorReference: string;
  actorDisplay?: string;
  comment?: string;
  // The offerings this clinician is bookable for. Each becomes a serviceType
  // entry (the reference link) AND a per-service SchedulingParameters block
  // carrying duration/alignment + the timezone + availability override.
  services: ScheduleOffering[];
  timezone: string;
  availability: Availability[];
  // Outer bound the engine clamps $find to and rejects $hold outside of —
  // fed by the clinician's PractitionerRole.period (employment window).
  planningHorizon?: Period;
  projectId?: string;
};

// A Schedule = one clinician's calendar for one or more offerings. R4 has no
// `name`, so a human label rides on `comment`. Per-service duration/alignment +
// availability + timezone live in one SchedulingParameters block each (the
// engine matches a block to a service by its `service` sub-reference).
export function clinicianSchedule(input: ClinicianScheduleInput): Schedule {
  return {
    resourceType: 'Schedule',
    id: input.id,
    ...(input.projectId ? { meta: { project: input.projectId } } : {}),
    active: true,
    ...(input.comment ? { comment: input.comment } : {}),
    actor: [
      {
        reference: input.actorReference,
        ...(input.actorDisplay ? { display: input.actorDisplay } : {}),
      },
    ],
    ...(input.planningHorizon
      ? { planningHorizon: input.planningHorizon }
      : {}),
    serviceType: input.services.map((s) => ({
      extension: [
        {
          url: SERVICE_TYPE_REFERENCE_EXTENSION,
          valueReference: {
            reference: s.reference,
            ...(s.display ? { display: s.display } : {}),
          },
        },
      ],
    })),
    extension: input.services.map((s) =>
      buildSchedulingParameters({
        service: {
          reference: s.reference,
          ...(s.display ? { display: s.display } : {}),
        },
        durationMinutes: s.durationMinutes,
        alignmentIntervalMinutes:
          s.alignmentIntervalMinutes ?? s.durationMinutes,
        ...(s.bufferBeforeMinutes !== undefined
          ? { bufferBeforeMinutes: s.bufferBeforeMinutes }
          : {}),
        ...(s.bufferAfterMinutes !== undefined
          ? { bufferAfterMinutes: s.bufferAfterMinutes }
          : {}),
        timezone: input.timezone,
        alignmentTimezone: input.timezone,
        availability: input.availability,
      }),
    ),
  };
}

// True when a Schedule's serviceType links to the given HealthcareService via
// the service-type-reference extension. Filters discovered schedules to the
// ones this availability model owns (ignoring legacy multi-actor schedules).
export function scheduleLinksToService(
  schedule: Schedule,
  healthcareServiceId: string,
): boolean {
  const target = `HealthcareService/${healthcareServiceId}`;
  return (schedule.serviceType ?? []).some((concept) =>
    (concept.extension ?? []).some(
      (e) =>
        e.url === SERVICE_TYPE_REFERENCE_EXTENSION &&
        e.valueReference?.reference === target,
    ),
  );
}

/**
 * True when a Schedule should still be served. The materializer parks a
 * clinician who has gone non-bookable by setting `active: false` rather than
 * deleting the Schedule — the id has to survive a bookable → not bookable →
 * bookable cycle because `Slot.schedule` is a 1..1 reference to it. Every read
 * path must therefore exclude inactive schedules itself; `$find` takes the
 * schedule id it is given and does not re-check `active`.
 */
export function isScheduleActive(schedule: Schedule): boolean {
  return schedule.active !== false;
}

// True when a Schedule carries ANY service-type reference — the marker that it
// belongs to this availability model (legacy multi-actor schedules carry none).
export function scheduleHasServiceReference(schedule: Schedule): boolean {
  return (schedule.serviceType ?? []).some((concept) =>
    (concept.extension ?? []).some(
      (e) => e.url === SERVICE_TYPE_REFERENCE_EXTENSION,
    ),
  );
}

// IANA timezone on a clinic Location (the root jurisdiction Location lacks it).
export function locationTimezone(
  location: { extension?: Extension[] } | undefined,
): string | undefined {
  return location?.extension?.find((e) => e.url === FHIR_TIMEZONE_EXTENSION)
    ?.valueCode;
}

// ── Hours ⇄ PractitionerRole.availableTime ──────────────────────────────────
export function isDayOfWeek(v: unknown): v is DayOfWeek {
  return (
    typeof v === 'string' && (DAYS_OF_WEEK as readonly string[]).includes(v)
  );
}

export function availabilityFromRole(role: PractitionerRole): Availability[] {
  return (role.availableTime ?? []).flatMap((a) => {
    const days = (a.daysOfWeek ?? []).filter(isDayOfWeek);
    if (days.length === 0 || !a.availableStartTime || !a.availableEndTime) {
      return [];
    }
    return [
      {
        daysOfWeek: days,
        start: a.availableStartTime,
        end: a.availableEndTime,
      },
    ];
  });
}

export function availableTimeFromAvailability(
  availability: Availability[],
): NonNullable<PractitionerRole['availableTime']> {
  return availability
    .filter((a) => a.daysOfWeek.length > 0)
    .map((a) => ({
      daysOfWeek: a.daysOfWeek,
      availableStartTime: a.start,
      availableEndTime: a.end,
    }));
}

// ── Time off ⇄ PractitionerRole.notAvailable ────────────────────────────────

/**
 * Identifier system marking a Slot the materializer derived from a clinician's
 * time off; the value is the owning PractitionerRole id. Reconciliation matches
 * on it so booking Slots are never touched.
 */
export const TIME_OFF_SLOT_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/fhir/time-off-slot`;

/** One authored block on a clinician's calendar. Dates are FHIR date|dateTime. */
export type TimeOff = {
  description: string;
  start?: string;
  end?: string;
};

/** Only a complete period projects to a Slot; a partial one is authoring-only. */
export type DatedTimeOff = TimeOff & { start: string; end: string };

export function isDatedTimeOff(timeOff: TimeOff): timeOff is DatedTimeOff {
  return Boolean(timeOff.start && timeOff.end);
}

export function timeOffFromRole(role: PractitionerRole): TimeOff[] {
  return (role.notAvailable ?? []).flatMap((entry) => {
    const description = entry.description?.trim();
    if (!description) return [];
    return [
      {
        description,
        ...(entry.during?.start ? { start: entry.during.start } : {}),
        ...(entry.during?.end ? { end: entry.during.end } : {}),
      },
    ];
  });
}

export function notAvailableFromTimeOff(
  timeOff: readonly TimeOff[],
): NonNullable<PractitionerRole['notAvailable']> {
  return timeOff.flatMap((entry) => {
    // `description` is 1..1 in FHIR — an entry without one fails the write.
    const description = entry.description.trim();
    if (!description) return [];
    const during = {
      ...(entry.start ? { start: entry.start } : {}),
      ...(entry.end ? { end: entry.end } : {}),
    };
    return [
      { description, ...(Object.keys(during).length > 0 ? { during } : {}) },
    ];
  });
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// "+05:30" / "-0700" / "+05" — the offset form several vendors persist as a
// clinic timezone (BLVD writes dayjs `format('Z')`, MovementX and Tebra
// hardcode "+00:00").
const FIXED_OFFSET = /^([+-])(\d{2})(?::?(\d{2}))?$/;

/**
 * Offset (ms) for an offset-form timezone, or null if it isn't one.
 *
 * `Intl` only began accepting offset identifiers in Chrome 120 / Firefox 121 /
 * Safari 17.4, and our build floor is Chrome 111 / Firefox 114 / Safari 16.4 —
 * below that they are unrecognized zone names and `DateTimeFormat` throws a
 * RangeError. Resolving them arithmetically keeps every supported engine on
 * the same path. Note this is fixed and DST-blind, which is what an offset
 * means — it does not make an offset a correct substitute for an IANA zone.
 */
function fixedOffsetMs(timezone: string): number | null {
  const match = FIXED_OFFSET.exec(timezone);
  if (!match) return null;
  const [, sign, rawHours, rawMinutes = '00'] = match;
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (hours > 23 || minutes > 59) return null;
  return (sign === '-' ? -1 : 1) * (hours * 60 + minutes) * 60_000;
}

// Offset (ms) between the zone's wall clock and UTC at a given instant.
function zoneOffsetMs(instant: number, timezone: string): number {
  const fixed = fixedOffsetMs(timezone);
  if (fixed !== null) return fixed;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return (
    Date.UTC(
      part('year'),
      part('month') - 1,
      part('day'),
      part('hour'),
      part('minute'),
      part('second'),
    ) - instant
  );
}

/**
 * The instant at which the clinic-local wall clock reads `minutes` past
 * midnight on `date` (+ `dayOffset` whole days).
 *
 * The whole wall-clock value is resolved in one two-pass sample. Offsetting a
 * correct midnight by `minutes` instead reintroduces the DST error whenever a
 * transition falls between midnight and the target — dropping a card on 10:00
 * on a spring-forward day would persist 11:00.
 */
export function clinicWallInstant(
  date: string,
  timezone: string,
  minutes = 0,
  dayOffset = 0,
): string {
  const [year, month, day] = date.split('-').map(Number);
  const wallClock = Date.UTC(year!, month! - 1, day! + dayOffset, 0, minutes);
  const guess = wallClock - zoneOffsetMs(wallClock, timezone);
  return new Date(wallClock - zoneOffsetMs(guess, timezone)).toISOString();
}

/**
 * The instant at which clinic-local midnight of `date` (+ `dayOffset` whole
 * days) falls.
 */
export function clinicMidnightInstant(
  date: string,
  timezone: string,
  dayOffset = 0,
): string {
  return clinicWallInstant(date, timezone, 0, dayOffset);
}

/**
 * A time-off period as the instants a `busy-unavailable` Slot needs. FHIR
 * `date` values are whole days in the clinic's zone, and an end DATE is
 * inclusive (the universal PMS reading: "off Mon–Fri" includes Friday), so it
 * resolves to clinic-local midnight of the following day. Values that already
 * carry a time are instants and pass through untouched.
 */
export function timeOffWindow(
  timeOff: TimeOff,
  timezone: string,
): { start: string; end: string } | undefined {
  const { start, end } = timeOff;
  if (!start || !end) return undefined;
  return {
    start: DATE_ONLY.test(start)
      ? clinicMidnightInstant(start, timezone)
      : start,
    end: DATE_ONLY.test(end) ? clinicMidnightInstant(end, timezone, 1) : end,
  };
}

// ── Display ─────────────────────────────────────────────────────────────────
export const DAY_SHORT_LABELS: Record<DayOfWeek, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const DAY_LABELS: Record<DayOfWeek, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

// "09:00:00" / "09:00" → "9:00 AM". Wall-clock in the schedule's timezone, so
// no Date and no locale — the string is already local to the clinic.
export function formatWallClock(time: string): string {
  const [rawHour = '', minute = '00'] = time.split(':');
  // Match digits rather than lean on Number(), which reads '' and whitespace
  // as 0 (a blank time would render as midnight) and '0x10' as 16.
  if (!/^\d{1,2}$/.test(rawHour)) return time;
  const hour = Number(rawHour);
  if (hour > 23) return time;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

/**
 * One `Location.hoursOfOperation` entry as a time range. `allDay`,
 * `openingTime` and `closingTime` are all optional in FHIR, so an entry
 * missing its bounds is unspecified — not midnight-to-midnight.
 */
export function formatOpeningHours(entry: LocationHoursOfOperation): string {
  if (entry.allDay) return 'All day';
  const { openingTime, closingTime } = entry;
  if (!openingTime || !closingTime) return 'Hours not set';
  return `${formatWallClock(openingTime)}–${formatWallClock(closingTime)}`;
}

// Weekday order is DAYS_OF_WEEK; runs of 3+ contiguous days collapse to a
// range ("Mon–Fri"), anything shorter stays a list ("Mon, Wed").
function formatDays(days: readonly DayOfWeek[]): string {
  const ordered = DAYS_OF_WEEK.filter((day) => days.includes(day));
  const parts: string[] = [];
  let runStart = 0;
  for (let i = 0; i <= ordered.length; i++) {
    const contiguous =
      i < ordered.length &&
      DAYS_OF_WEEK.indexOf(ordered[i]!) ===
        DAYS_OF_WEEK.indexOf(ordered[i - 1]!) + 1;
    if (i > 0 && !contiguous) {
      const run = ordered.slice(runStart, i);
      parts.push(
        run.length >= 3
          ? `${DAY_SHORT_LABELS[run[0]!]}–${DAY_SHORT_LABELS[run[run.length - 1]!]}`
          : run.map((day) => DAY_SHORT_LABELS[day]).join(', '),
      );
      runStart = i;
    }
  }
  return parts.join(', ');
}

/**
 * Human summary of recurring weekly hours: "Mon–Fri 9:00 AM–5:00 PM", several
 * windows joined by "·". Empty availability returns undefined — callers decide
 * what "no hours" reads as, since it carries meaning (not bookable).
 */
export function formatAvailability(
  availability: readonly Availability[],
): string | undefined {
  const windows = availability.filter(
    (window) => window.daysOfWeek.length > 0 && window.start && window.end,
  );
  if (windows.length === 0) return undefined;
  return windows
    .map(
      (window) =>
        `${formatDays(window.daysOfWeek)} ${formatWallClock(window.start)}–${formatWallClock(window.end)}`,
    )
    .join(' · ');
}

// True when the offering is bookable at the clinic — `[]` means "all locations".
export function offeringBookableAt(
  hs: HealthcareService,
  locationId: string,
): boolean {
  const locs = hs.location ?? [];
  if (locs.length === 0) return true;
  return locs.some((l) => l.reference === `Location/${locationId}`);
}
